import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { requiereCuit, validarClienteNuevo } from "../../servicios/facturacionFiscal";
import { puedeAdministrarClientes, puedeCrearClientes } from "../../servicios/sesionSeguridad";
import { useStorePos } from "./useStorePos";

const VACIO = {
  Id: 0,
  sCodigo: "",
  sDescripcion: "",
  CondicionIVAId: "",
  Documento: "",
  TipoDocumentoId: "",
};

const PosClienteNuevo = () => {
  const { t } = useTranslation();
  const visible = useStorePos((state) => state.ClienteNuevoVisible);
  const clienteNuevoNombre = useStorePos((state) => state.ClienteNuevoNombre);
  const clienteEdicionId = useStorePos((state) => state.ClienteEdicionId);
  const clienteEditable = useStorePos((state) => state.ClienteEditable);
  const cargandoCliente = useStorePos((state) => state.CargandoClienteEditable);
  const tiposDocumento = useStorePos((state) => state.TiposDocumento);
  const condicionesIva = useStorePos((state) => state.CondicionesIVA);
  const cargandoTipos = useStorePos((state) => state.CargandoTiposDocumento);
  const cargandoCondiciones = useStorePos((state) => state.CargandoCondicionesIVA);
  const guardando = useStorePos((state) => state.GuardandoCliente);
  const solicitaIdentificacionFiscal = useStorePos((state) => state.SolicitaIdentificacionFiscal);
  const [cliente, setCliente] = useState(VACIO);
  const [errores, setErrores] = useState({});
  const editando = Number(clienteEdicionId) > 0;
  const autorizado = editando ? puedeAdministrarClientes() : puedeCrearClientes();

  useEffect(() => {
    if (!visible || !autorizado) return;
    setErrores({});
    useStorePos.getState().cargarTiposDocumento();
    useStorePos.getState().cargarCondicionesIVA();
    if (!editando) {
      setCliente({ ...VACIO, sDescripcion: clienteNuevoNombre });
    } else if (Number(clienteEditable?.Id) === Number(clienteEdicionId)) {
      setCliente(clienteEditable);
    } else {
      setCliente(VACIO);
    }
  }, [autorizado, clienteEditable, clienteEdicionId, clienteNuevoNombre, editando, visible]);

  const condicion = useMemo(
    () => condicionesIva.find((item) => Number(item.Id) === Number(cliente.CondicionIVAId)),
    [cliente.CondicionIVAId, condicionesIva]
  );
  const documentoObligatorio = editando || solicitaIdentificacionFiscal;
  const requiereDocumentoCuit = documentoObligatorio &&
    (solicitaIdentificacionFiscal || (condicion && requiereCuit(condicion)));

  if (!visible) return null;

  const cambiar = (campo, valor) => {
    setCliente((actual) => ({ ...actual, [campo]: valor }));
    setErrores((actual) => ({ ...actual, [campo]: "" }));
  };

  const guardar = async (event) => {
    event.preventDefault();
    if (!autorizado) return;
    const validacion = validarClienteNuevo(cliente, tiposDocumento, condicionesIva, {
      exigirCuit: solicitaIdentificacionFiscal,
      exigirDocumento: documentoObligatorio,
    });
    setErrores(validacion.errores);
    if (!validacion.valido) return;
    const ok = editando
      ? await useStorePos.getState().actualizarCliente(cliente)
      : await useStorePos.getState().crearCliente(cliente, {
          exigirCuit: solicitaIdentificacionFiscal,
          exigirDocumento: documentoObligatorio,
        });
    if (ok) setCliente(VACIO);
  };

  const cerrar = () => useStorePos.getState().cerrarFormularioCliente();

  if (!autorizado) return null;

  return (
    <div
      className="pos-modal-backdrop pos-cliente-modal-fondo"
      role="presentation"
      onKeyDown={(event) => {
        if (event.key === "Escape") cerrar();
      }}
    >
      <form
        className="pos-modal pos-cliente-nuevo"
        onSubmit={guardar}
        aria-label={editando ? t("customers.editForm") : t("customers.createForm")}
        role="dialog"
        aria-modal="true"
      >
        <div className="pos-modal-header pos-cliente-header">
          <div className="pos-cliente-heading">
            <span className="pos-cliente-header-icon" aria-hidden="true">
              <i className={editando ? "pi pi-user-edit" : "pi pi-user-plus"} />
            </span>
            <div>
              <span className="pos-step">{t("customers.fiscalCustomer")}</span>
              <h3>{editando ? t("customers.editCustomer") : t("customers.newCustomer")}</h3>
              <p>
                {editando
                  ? t("customers.editHelp")
                  : t("customers.createHelp")}
              </p>
            </div>
          </div>
          <button type="button" className="pos-icon-btn pos-cliente-cerrar" onClick={cerrar} aria-label={t("customers.closeForm")} disabled={guardando}>
            <i className="pi pi-times" />
          </button>
        </div>

        <div className="pos-cliente-contenido">
          <section className="pos-cliente-seccion" aria-labelledby="cliente-datos-basicos">
            <div className="pos-cliente-seccion-header">
              <span className="pos-cliente-seccion-icon" aria-hidden="true"><i className="pi pi-user" /></span>
              <div>
                <h4 id="cliente-datos-basicos">{t("customers.customerData")}</h4>
              </div>
            </div>

            <div className="pos-modal-grid pos-cliente-grid-basico">
              {editando && (
                <label className="pos-cliente-campo pos-cliente-codigo">
                  <span className="pos-cliente-label">{t("common.code")}</span>
                  <span className="pos-cliente-input-icon">
                    <i className="pi pi-hashtag" aria-hidden="true" />
                    <input value={cliente.sCodigo} readOnly disabled={cargandoCliente} aria-label={t("customers.customerCode")} />
                  </span>
                  <small>{t("customers.internalIdentifier")}</small>
                </label>
              )}
              <label className={`pos-cliente-campo ${editando ? "" : "pos-campo-ancho"} ${errores.sDescripcion ? "tiene-error" : ""}`}>
                <span className="pos-cliente-label">{t("customers.businessNameOrName")} <b aria-hidden="true">*</b></span>
                <span className="pos-cliente-input-icon">
                  <i className="pi pi-building" aria-hidden="true" />
                  <input
                    autoFocus
                    autoComplete="organization"
                    placeholder={t("customers.businessNamePlaceholder")}
                    value={cliente.sDescripcion}
                    disabled={cargandoCliente}
                    aria-invalid={Boolean(errores.sDescripcion)}
                    onChange={(e) => cambiar("sDescripcion", e.target.value)}
                  />
                </span>
                {errores.sDescripcion && <small className="pos-linea-error"><i className="pi pi-exclamation-circle" aria-hidden="true" />{errores.sDescripcion}</small>}
              </label>
            </div>
          </section>

          <section className="pos-cliente-seccion" aria-labelledby="cliente-datos-fiscales">
            <div className="pos-cliente-seccion-header">
              <span className="pos-cliente-seccion-icon fiscal" aria-hidden="true"><i className="pi pi-receipt" /></span>
              <div>
                <h4 id="cliente-datos-fiscales">{t("customers.fiscalData")}</h4>
              </div>
            </div>

            <div className="pos-modal-grid">
              <label className={`pos-cliente-campo pos-campo-ancho ${errores.CondicionIVAId ? "tiene-error" : ""}`}>
                <span className="pos-cliente-label">{t("pos.vatConditionLong")} <b aria-hidden="true">*</b></span>
                <span className="pos-cliente-select-wrap">
                  <select
                    value={cliente.CondicionIVAId}
                    disabled={cargandoCliente || cargandoCondiciones}
                    aria-invalid={Boolean(errores.CondicionIVAId)}
                    onChange={(e) => cambiar("CondicionIVAId", e.target.value)}
                  >
                    <option value="">{cargandoCondiciones ? t("customers.loadingConditions") : t("customers.selectCondition")}</option>
                    {condicionesIva.map((item) => (
                      <option key={item.Id} value={item.Id}>{item.sDescripcion || item.Descripcion}</option>
                    ))}
                  </select>
                </span>
                {errores.CondicionIVAId && <small className="pos-linea-error"><i className="pi pi-exclamation-circle" aria-hidden="true" />{errores.CondicionIVAId}</small>}
              </label>
              <label className={`pos-cliente-campo ${errores.TipoDocumentoId ? "tiene-error" : ""}`}>
                <span className="pos-cliente-label">{t("pos.documentType")} {documentoObligatorio && <b aria-hidden="true">*</b>}</span>
                <span className="pos-cliente-select-wrap">
                  <select
                    value={cliente.TipoDocumentoId}
                    disabled={cargandoCliente || cargandoTipos}
                    aria-invalid={Boolean(errores.TipoDocumentoId)}
                    onChange={(e) => cambiar("TipoDocumentoId", e.target.value)}
                  >
                    <option value="">{cargandoTipos ? t("customers.loadingDocumentTypes") : t("customers.selectDocumentType")}</option>
                    {tiposDocumento.map((tipo) => (
                      <option key={tipo.Id} value={tipo.Id}>{tipo.sDescripcion || tipo.Descripcion}</option>
                    ))}
                  </select>
                </span>
                {errores.TipoDocumentoId && <small className="pos-linea-error"><i className="pi pi-exclamation-circle" aria-hidden="true" />{errores.TipoDocumentoId}</small>}
              </label>
              <label className={`pos-cliente-campo ${errores.Documento ? "tiene-error" : ""}`}>
                <span className="pos-cliente-label">
                  {requiereDocumentoCuit ? "CUIT" : t("pos.document")} {documentoObligatorio && <b aria-hidden="true">*</b>}
                </span>
                <span className="pos-cliente-input-icon">
                  <i className="pi pi-id-card" aria-hidden="true" />
                  <input
                    inputMode={requiereDocumentoCuit ? "numeric" : "text"}
                    autoComplete="off"
                    maxLength={requiereDocumentoCuit ? 13 : undefined}
                    placeholder={requiereDocumentoCuit ? "20-12345678-9" : t("customers.enterNumber")}
                    value={cliente.Documento}
                    disabled={cargandoCliente}
                    aria-invalid={Boolean(errores.Documento)}
                    onChange={(e) => cambiar("Documento", e.target.value.replace(/[^\d-]/g, ""))}
                  />
                </span>
                {!errores.Documento && requiereDocumentoCuit && <small>{t("customers.taxIdFormatHelp")}</small>}
                {!errores.Documento && !documentoObligatorio && <small>{t("customers.optionalDocumentHelp")}</small>}
                {errores.Documento && <small className="pos-linea-error"><i className="pi pi-exclamation-circle" aria-hidden="true" />{errores.Documento}</small>}
              </label>
            </div>
          </section>
        </div>

        <div className="pos-modal-actions pos-cliente-acciones">
          <p><i className="pi pi-shield" aria-hidden="true" /> {t("customers.billingPrivacy")}</p>
          <div>
            <button type="button" className="pos-btn-secundario" onClick={cerrar} disabled={guardando}>{t("common.cancel")}</button>
          <button type="submit" className="pos-btn-principal" disabled={guardando || cargandoCliente || cargandoTipos || cargandoCondiciones}>
              <i className={guardando ? "pi pi-spin pi-spinner" : "pi pi-check"} aria-hidden="true" />
              {guardando ? t("common.saving") : editando ? t("customers.saveChanges") : t("customers.createCustomer")}
          </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PosClienteNuevo;
